/**
 * Application route paths and React Router patterns.
 * Use {@link AppRoute} for static navigations; use builders for project-scoped URLs.
 */

import type { ProjectDetailsResponse } from "@/types/project";

/** Fixed paths (no dynamic segments). */
export enum AppRoute {
  Home = "/",
  LoginCallback = "/login/callback",
  Test = "/test",
  TestVideo = "/test-video",
  TestVideoAnalysis = "/test-video-analysis",
  TestVideoExtend = "/test-video-extend",
  TestVideoCut = "/test-video-cut",
  TestCharacter = "/test-character",
  TestStyleDna = "/test-style-dna"
}

/**
 * `Route path` values for {@link Routes} / {@link Route} (react-router-dom).
 * Param names match {@link useParams}.
 */
export enum AppRoutePattern {
  Home = "/",
  LoginCallback = "/login/callback",
  /** Optional `:entityId` — character / location / storyboard-shot id for bookmarkable selection. */
  ProjectPanel = "/:projectId/:panel/:entityId?",
  ProjectRoot = "/:projectId",
  Test = "/test",
  TestVideo = "/test-video",
  TestVideoAnalysis = "/test-video-analysis",
  TestVideoExtend = "/test-video-extend",
  TestVideoCut = "/test-video-cut",
  TestCharacter = "/test-character",
  TestStyleDna = "/test-style-dna"
}

/**
 * `:panel` path segment for {@link AppRoutePattern.ProjectPanel}.
 * Use these values for nav keys and route comparisons so URLs stay consistent.
 */
export enum ProjectPanelRoute {
  Dashboard = "dashboard",
  Script = "script",
  Breakdown = "breakdown",
  Characters = "characters",
  Locations = "locations",
  Props = "props",
  ImageFrame = "imageFrame",
  Video = "video",
  Dialogue = "dialogue",
  Audio = "audio",
  Timeline = "timeline",
  Settings = "settings",
  /** Not in main nav; Style DNA workspace (header / deep link). */
  StyleDna = "styleDna"
}

/** Panels whose URL includes a third segment: entity `id` from project JSON. */
export const PROJECT_PANEL_ENTITY_SLUG_KEYS = new Set<string>([
  ProjectPanelRoute.Characters,
  ProjectPanelRoute.Locations,
  ProjectPanelRoute.ImageFrame,
  ProjectPanelRoute.Video
]);

export function projectPanelUsesEntitySlug(panel: string): boolean {
  return PROJECT_PANEL_ENTITY_SLUG_KEYS.has(panel);
}

const encode = (segment: string): string => encodeURIComponent(segment);

/**
 * `/projectId/panel` or `/projectId/panel/entityId` when `panel` uses an entity slug and `entityId` is non-empty.
 */
export function projectPanelPath(projectId: string, panel: string, entityId?: string | null): string {
  const base = `/${encode(projectId)}/${encode(panel)}`;
  const slug = typeof entityId === "string" && entityId.trim().length > 0 ? entityId.trim() : null;
  if (slug !== null && projectPanelUsesEntitySlug(panel)) {
    return `${base}/${encode(slug)}`;
  }

  return base;
}

/** Active list index for the URL slug on entity-slug panels; `null` if URL should not drive the store. */
export function activeIndexForProjectPanelEntity(
  panel: string,
  details: ProjectDetailsResponse | null,
  entityIdFromUrl: string | undefined
): number | null {
  if (!details?.project || !projectPanelUsesEntitySlug(panel)) {
    return null;
  }

  if (entityIdFromUrl === undefined || entityIdFromUrl.length === 0) {
    return null;
  }

  const p = details.project;

  if (panel === ProjectPanelRoute.Characters) {
    const list = p.characters ?? [];
    const idx = list.findIndex((c) => c.id === entityIdFromUrl);
    return idx === -1 ? 0 : idx;
  }

  if (panel === ProjectPanelRoute.Locations) {
    const list = p.locations ?? [];
    const idx = list.findIndex((l) => l.id === entityIdFromUrl);
    return idx === -1 ? 0 : idx;
  }

  if (panel === ProjectPanelRoute.ImageFrame || panel === ProjectPanelRoute.Video) {
    const list = p["storyboard-shots"] ?? [];
    const idx = list.findIndex((s) => s.id === entityIdFromUrl);
    return idx === -1 ? 0 : idx;
  }

  return null;
}

/** True when `entityId` matches a real row on this panel (not a stale / typo slug). */
export function entityIdExistsInProjectPanel(panel: string, details: ProjectDetailsResponse | null, entityIdFromUrl: string | undefined): boolean {
  if (!details?.project || !projectPanelUsesEntitySlug(panel) || entityIdFromUrl === undefined || entityIdFromUrl.length === 0) {
    return false;
  }

  const p = details.project;

  if (panel === ProjectPanelRoute.Characters) {
    return (p.characters ?? []).some((c) => c.id === entityIdFromUrl);
  }

  if (panel === ProjectPanelRoute.Locations) {
    return (p.locations ?? []).some((l) => l.id === entityIdFromUrl);
  }

  if (panel === ProjectPanelRoute.ImageFrame || panel === ProjectPanelRoute.Video) {
    return (p["storyboard-shots"] ?? []).some((s) => s.id === entityIdFromUrl);
  }

  return false;
}

/** Entity id segment for the current MobX indices and panel (canonical URL). */
export function entitySlugForProjectPanel(
  panel: string,
  details: ProjectDetailsResponse | null,
  activeStoryboardShotIndex: number,
  activeCharacterIndex: number,
  activeLocationIndex: number
): string | undefined {
  if (!details?.project || !projectPanelUsesEntitySlug(panel)) {
    return undefined;
  }

  const p = details.project;

  if (panel === ProjectPanelRoute.Characters) {
    const list = p.characters ?? [];
    if (list.length === 0) {
      return undefined;
    }

    const idx = Math.min(Math.max(0, activeCharacterIndex), list.length - 1);
    return list[idx]?.id;
  }

  if (panel === ProjectPanelRoute.Locations) {
    const list = p.locations ?? [];
    if (list.length === 0) {
      return undefined;
    }

    const idx = Math.min(Math.max(0, activeLocationIndex), list.length - 1);
    return list[idx]?.id;
  }

  if (panel === ProjectPanelRoute.ImageFrame || panel === ProjectPanelRoute.Video) {
    const list = p["storyboard-shots"] ?? [];
    if (list.length === 0) {
      return undefined;
    }

    const idx = Math.min(Math.max(0, activeStoryboardShotIndex), list.length - 1);
    return list[idx]?.id;
  }

  return undefined;
}

/** Deep link for a generation notification from `tag_type` + `tag_id` (character / location / shot / video). */
export function projectPanelPathForTransactionTag(
  projectId: string,
  tagType: string | null | undefined,
  tagId: string | null | undefined
): string | null {
  const entityId = typeof tagId === "string" ? tagId.trim() : "";
  if (entityId.length === 0) {
    return null;
  }

  const normalizedTagType = typeof tagType === "string" ? tagType.trim().toLowerCase() : "";

  switch (normalizedTagType) {
    case "character":
      return projectPanelPath(projectId, ProjectPanelRoute.Characters, entityId);
    case "location":
      return projectPanelPath(projectId, ProjectPanelRoute.Locations, entityId);
    case "shot":
      return projectPanelPath(projectId, ProjectPanelRoute.ImageFrame, entityId);
    case "video":
      return projectPanelPath(projectId, ProjectPanelRoute.Video, entityId);
    default:
      return null;
  }
}

export type LibraryMediaKind = "image" | "video";

export interface LibraryMediaHighlight {
  readonly kind: LibraryMediaKind;
  readonly mediaId: string;
}

const LIBRARY_MEDIA_TYPE_QUERY = "type";
const LIBRARY_IMAGE_ID_QUERY = "image_id";
const LIBRARY_VIDEO_ID_QUERY = "video_id";

export function appendLibraryMediaHighlightToPath(path: string, highlight: LibraryMediaHighlight): string {
  const params = new URLSearchParams();
  params.set(LIBRARY_MEDIA_TYPE_QUERY, highlight.kind);

  if (highlight.kind === "image") {
    params.set(LIBRARY_IMAGE_ID_QUERY, highlight.mediaId);
  } else {
    params.set(LIBRARY_VIDEO_ID_QUERY, highlight.mediaId);
  }

  return `${path}?${params.toString()}`;
}

export function parseLibraryMediaHighlightFromSearch(search: string): LibraryMediaHighlight | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const kind = params.get(LIBRARY_MEDIA_TYPE_QUERY)?.trim().toLowerCase();

  if (kind === "image") {
    const imageId = params.get(LIBRARY_IMAGE_ID_QUERY)?.trim();
    return imageId ? { kind: "image", mediaId: imageId } : null;
  }

  if (kind === "video") {
    const videoId = params.get(LIBRARY_VIDEO_ID_QUERY)?.trim();
    return videoId ? { kind: "video", mediaId: videoId } : null;
  }

  return null;
}

export function searchWithoutLibraryMediaHighlight(search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete(LIBRARY_MEDIA_TYPE_QUERY);
  params.delete(LIBRARY_IMAGE_ID_QUERY);
  params.delete(LIBRARY_VIDEO_ID_QUERY);
  const next = params.toString();
  return next.length > 0 ? `?${next}` : "";
}
